import { useParams, useSearchParams } from 'react-router-dom';
import { useBackNavigate } from '../../../shared/hooks/useBackNavigate';
import { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../../../shared/components/ui/button';
import { useContactCardOpen } from '@/features/contact-card-share/hooks/useContactCardOpen';
import { useProviderContactCardShare } from '@/features/contact-card-share/hooks/useProviderContactCardShare';
import {
  useProviderBase,
  useProviderServices,
  useProviderQualifications,
  useProviderMedia,
  useProviderReviews,
  useProviderReviewsSummary,
} from '../hooks/useProviderSearch';
import { useAuth } from '../../authentication/hooks/useAuth';
import { useWorkspace } from '@/features/workspace-sub-delegation';
import { useGuestActionGate } from '@/shared/hooks/useGuestActionGate';
import { useCheckConnection } from '../../network/hooks/useNetwork';
import { useProfessionalFavoriteStatus } from '../../favorites/hooks/useFavorites';
import { useProviderDetailsActions } from '../hooks/useProviderDetailsActions';
import { getProviderDisplayName } from '../utils/formatters';
import { useProviderResources } from '../hooks/useProviderResources';
import { useContactAction } from '../hooks/useContactAction';
import { useCreateIntakeFormEngagement } from '../../consultations/hooks/useIntakeFormEngagement';
import { ProviderDetailsContent } from './ProviderDetailsContent';
import { ProviderDetailsModals } from './ProviderDetailsModals';
import { PreChatIntentModalPrototype } from './prototypes/PreChatIntentModalPrototype';
import { useProviderProfileView } from '../hooks/useProviderProfileView';
import { parseDiscoverySource } from '../api/profileViewApi';

export const ProviderDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const searchContextId = searchParams.get('searchContextId') ?? undefined;
  const discoverySource = parseDiscoverySource(searchParams.get('discoverySource'));
  // Feature 102: referral marker from the referral email's profile link.
  const referralMarker = searchParams.get('referral');
  const profileViewEngagementId = useProviderProfileView(
    id,
    searchContextId,
    discoverySource,
  );
  // Feature 071: this page is one of the two the card email links to.
  useContactCardOpen();

  const goBack = useBackNavigate();
  const { user } = useAuth();
  // For SUB_DELEGATE users, act under the workspace owner's role + granted
  // permissions. effectiveRole falls back to the literal role for everyone else.
  const { effectiveRole, hasWorkspacePermission } = useWorkspace();
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [callRequestModalOpen, setCallRequestModalOpen] = useState(false);
  const [calendlyModalOpen, setCalendlyModalOpen] = useState(false);
  const [intakeFormModalOpen, setIntakeFormModalOpen] = useState(false);
  const [showSharePrompt, setShowSharePrompt] = useState(false); // Feature 047

  // Scroll to top when page loads or provider ID changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  
  // Load all data in parallel on page open - each section has independent lifecycle
  const { data: provider, isLoading, isError } = useProviderBase(id, true);
  const { data: servicesData, isLoading: servicesLoading } = useProviderServices(id, true);
  const { data: qualificationsData, isLoading: qualificationsLoading } = useProviderQualifications(id, true);
  const { data: mediaData, isLoading: mediaLoading } = useProviderMedia(id, true);
  
  // Lightweight reviews summary for hero section (just rating + count)
  const { data: reviewsSummaryData, isLoading: reviewsSummaryLoading } = useProviderReviewsSummary(id, true);
  
  // Full reviews data for Reviews tab (loaded on page open for better UX)
  const { data: reviewsData, isLoading: reviewsLoading, isError: reviewsError } = useProviderReviews(id, true);

  // Feature 011 - US1: Load provider's authored resources.
  // Resources store the authoring User's id in author_pro_id, so we must query
  // by the provider's User id — NOT the ServiceProvider id from the URL.
  const { data: resourcesData, isLoading: resourcesLoading } = useProviderResources(
    provider?.user.id,
    !!provider?.user.id,
  );

  // Check user roles (effective role for delegates). Guests see care-seeker
  // contact actions, but clicking them opens the signup modal.
  const role = effectiveRole ?? user?.role;
  const isGuest = !user;
  const canOperate = hasWorkspacePermission('WORKSPACE_OPERATIONS');
  const canAddToNetwork = (role === 'SERVICE_PROVIDER' || role === 'FINANCIAL_ADVISOR') && canOperate;
  const canFavorite = role === 'CARE_SEEKER' && canOperate;
  const canReferProvider = (role === 'FINANCIAL_ADVISOR' || role === 'SERVICE_PROVIDER') && canOperate;
  // Feature 103 (The Key pilot): decides whether the contact buttons route to
  // the embedded intake form, Calendly, or the default consultation-request
  // flow. Eligibility mirrors the existing consultation-button gate
  // (canFavorite) — same contract as the search card's canSchedule. Guests
  // never reach this: gateGuestAction short-circuits their clicks first.
  const contactAction = useContactAction(provider, canFavorite);
  const createIntakeFormEngagement = useCreateIntakeFormEngagement();
  // Guest gate: any contact action prompts account creation, deep-linking back
  // to this profile post-signup (action_button_click + providerType).
  const { openProviderContactGate } = useGuestActionGate();
  const gateGuestAction = () => {
    openProviderContactGate({
      providerId: id,
      providerType: 'SERVICE_PROVIDER',
    });
  };
  
  // Network and favorites status
  const { data: connectionData } = useCheckConnection(provider?.user.id || '', canAddToNetwork);
  const { data: favoriteStatusData } = useProfessionalFavoriteStatus(
    provider?.user.id || '',
    canFavorite && !!provider?.user.id,
  );
  
  // Feature 047: Handle network added callback
  const handleNetworkAdded = () => {
    setShowSharePrompt(true);
  };

  // FR-007e: Display name fallback chain - partnerCompany.name → companyName → (firstName + lastName)
  const providerDisplayName = provider ? getProviderDisplayName(provider.partnerCompany, provider.companyName, provider.user) : '';

  // Feature 071. Both pro roles can send a card; the FP gets a button in the
  // hero, the SP reaches it from inside their referral modal.
  const contactCard = useProviderContactCardShare({
    provider,
    role,
    canOperate,
    displayName: providerDisplayName,
  });

  // Provider actions hook
  const {
    effectiveNetworkState,
    effectiveFavoriteState,
    handleNetworkToggle,
    handleFavoriteToggle,
    handleMessageProvider,
    handleShareProvider,
    messageModal,
    isNetworkLoading,
    isFavoriteLoading,
  } = useProviderDetailsActions({
    providerUserId: provider?.user.id || '',
    serviceProviderId: id || '',
    providerName: providerDisplayName,
    professionalUserId: provider?.user.id || '',
    currentNetworkState: connectionData?.isConnected || false,
    currentFavoriteState: favoriteStatusData?.isFavorite || false,
    canAddToNetwork,
    canFavorite,
    onNetworkAdded: handleNetworkAdded, // Feature 047
    profileViewEngagementId,
  });

  const handleBack = () => {
    goBack('/search-providers');
  };

  const handleReferProvider = () => {
    setReferralModalOpen(true);
  };

  // Feature 047: Handle share confirmation
  const handleConfirmShare = () => {
    setShowSharePrompt(false);
    setTimeout(() => setReferralModalOpen(true), 100);
  };

  const handleCancelShare = () => {
    setShowSharePrompt(false);
  };

  // Feature 103 (wayfinder ticket 18): records the billable engagement at
  // the moment of the click, then opens the modal regardless of the call's
  // outcome — intent is captured even if the iframe later fails to load.
  const openIntakeFormModal = () => {
    if (id) {
      createIntakeFormEngagement.mutate({
        providerId: id,
        profileViewEngagementId: profileViewEngagementId ?? undefined,
      });
    }
    setIntakeFormModalOpen(true);
  };

  const handleRequestConsultation = () => {
    if (!provider) return;

    // Feature 103: The Key pilot's intake form takes precedence; every other
    // provider gets the Request-a-call modal (Feature 102). Calendly is now a
    // secondary action (handleScheduleConsultation below).
    if (contactAction.primary === 'intake-form') {
      openIntakeFormModal();
    } else {
      setCallRequestModalOpen(true);
    }
  };

  // Feature 102: secondary "Schedule Consultation" action for providers with
  // a calendlyUrl — opens the existing Calendly booking modal.
  const handleScheduleConsultation = () => {
    setCalendlyModalOpen(true);
  };

  // Feature 103: for The Key pilot providers, "Message Provider" opens the
  // same intake form modal as "Schedule/Request Consultation" instead of the
  // messaging flow. Untouched for every other provider (falls through to the
  // existing message handler).
  const handleMessageOrIntakeForm = () => {
    if (contactAction.primary === 'intake-form') {
      openIntakeFormModal();
      return;
    }
    handleMessageProvider();
  };

  // Show error state only if there's actually an error (not just loading)
  if (isError && !isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Provider Not Found</h2>
          <p className="text-gray-600 mb-6">The provider you're looking for doesn't exist or is no longer available.</p>
          <Button onClick={handleBack} className="bg-[#026857] hover:bg-[#025247]">
            Back to Search
          </Button>
        </div>
      </div>
    );
  }

  // Use lightweight reviews summary for hero section (optimized performance)
  const displayRating = reviewsSummaryData?.averageRating ?? provider?.averageRating ?? null;
  const displayReviewCount = reviewsSummaryData?.totalReviewCount ?? provider?.totalReviewCount ?? 0;
  
  return (
    <div className="min-h-screen bg-background">
      {/* Back Button */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <button onClick={handleBack} className="hover:opacity-80 transition-opacity">
            <div className="flex items-center gap-2">
              <ChevronLeft className="w-5 h-5 text-[#026857]" />
              <span className="text-[#026857]">Back to Results</span>
            </div>
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {provider && (
          <ProviderDetailsContent
            provider={provider}
            isLoading={isLoading}
            servicesData={servicesData}
            servicesLoading={servicesLoading}
            qualificationsData={qualificationsData}
            qualificationsLoading={qualificationsLoading}
            mediaData={mediaData}
            mediaLoading={mediaLoading}
            reviewsData={reviewsData}
            reviewsLoading={reviewsLoading}
            reviewsError={reviewsError}
            resourcesData={resourcesData}
            resourcesLoading={resourcesLoading}
            displayRating={displayRating}
            displayReviewCount={displayReviewCount}
            reviewsSummaryLoading={reviewsSummaryLoading}
            canFavorite={canFavorite || isGuest}
            canAddToNetwork={canAddToNetwork}
            canReferProvider={canReferProvider}
            currentFavoriteState={effectiveFavoriteState}
            currentNetworkState={effectiveNetworkState}
            isFavoriteLoading={isFavoriteLoading}
            isNetworkLoading={isNetworkLoading}
            onFavoriteToggle={isGuest ? gateGuestAction : handleFavoriteToggle}
            onNetworkToggle={handleNetworkToggle}
            onReferProvider={handleReferProvider}
            onShareContactCard={
              contactCard.canShare && role === 'FINANCIAL_ADVISOR'
                ? contactCard.openShare
                : undefined
            }
            onMessageProvider={isGuest ? gateGuestAction : handleMessageOrIntakeForm}
            onShareProvider={handleShareProvider}
            onRequestConsultation={isGuest ? gateGuestAction : handleRequestConsultation}
            onScheduleConsultation={isGuest ? gateGuestAction : handleScheduleConsultation}
          />
        )}
      </div>

      {provider && (
        <ProviderDetailsModals
          provider={provider}
          role={role}
          canReferProvider={canReferProvider}
          canFavorite={canFavorite}
          providerDisplayName={providerDisplayName}
          profileViewEngagementId={profileViewEngagementId ?? undefined}
          referralModalOpen={referralModalOpen}
          setReferralModalOpen={setReferralModalOpen}
          callRequestModalOpen={callRequestModalOpen}
          setCallRequestModalOpen={setCallRequestModalOpen}
          referralMarker={referralMarker}
          calendlyModalOpen={calendlyModalOpen}
          setCalendlyModalOpen={setCalendlyModalOpen}
          intakeFormModalOpen={intakeFormModalOpen}
          setIntakeFormModalOpen={setIntakeFormModalOpen}
          showSharePrompt={showSharePrompt}
          setShowSharePrompt={setShowSharePrompt}
          onConfirmShare={handleConfirmShare}
          onCancelShare={handleCancelShare}
          contactCard={contactCard}
        />
      )}
      {!import.meta.env.PROD && provider && searchParams.has('intentPrototype') && <PreChatIntentModalPrototype providerName={providerDisplayName} />}
      {messageModal}
    </div>
  );
};
